<template>
  <div data-view="billing">
    <router-view />

    <layout-logged-frame has-before :title="$t('accounts')">
      <template v-slot:header-actions>
        <Can
          action="upload"
          subject="billing"
        >
          <base-file-upload
            :trigger-label="$t('upload_csv')"
            accept=".csv"
            outlined
            :on-filter="filterFile"
            :on-change="onUploadFile"
          />
        </Can>

        <Can
          action="store"
          subject="billing"
        >
          <v-btn
            color="primary"
            @click="$router.push({ name: 'billing-new' })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_account') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs :tabs="navigationTabs"/>
      </template>

      <template v-slot:content>
        <BillingFilters
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <BillingTable
          :items="billing"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          :on-select-item="selectItem"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import BillingFilters from '../../components/forms/filters/Billing';
  import { isStr, isNum } from '@/utils';
  import navigationTabs from '../../config/billing/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs'
  import columns from '../../config/billing/columns';
  import BillingTable from '../../components/table/Billing';

  export default {
    components: {
      BillingFilters,
      NavigationTabs,
      BillingTable
    },
    data() {
      return {
        columns,
        navigationTabs,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'updated_at',
          totalItems: 0,
          search: ''
        },
        totalPages: 0,
        query: {},
        isLoading: false,
      };
    },
    computed: {
      billing() {
        return this.$store.getters['billing/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        if (
          to.query?.status === 'success' &&
          (from.name === 'billing-new' || from.name === 'billing-edit') &&
          to.name === 'billing'
        ) {
          this.getItems(this.query);
        }
      },
    },
    created() {
      this.getCountries();
      this.getEuCountries();
    },
    methods: {
      getItems({ page = 1, order = 'desc', sort = '', search = '', business = -1, reviewItems = [], externalId = '', ...rest } = {}) {
        const query = {
          page,
          order,
          sort,
          search,
          business,
          reviewItems,
          externalId,
          ...rest
        };

        this.isLoading = true;
        this.pagination.order = order;

        return this.$store.dispatch('billing/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['billing/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectItem(item) {
        this.$store.dispatch('billing/setActiveItem', item);
        this.$router.push({ name: 'billing-edit', params: { id: item.id } });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy
        };

        this.query = nextQuery;
        this.getItems(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy
        };

        this.query = nextQuery;
        this.getItems(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          page: 1,
          ...this.getSearchQuery(filters)
        };

        this.getItems(this.query);
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = { sort: sortBy };

        this.getItems(this.query);
      },
      getSearchQuery({ business, ...rest }) {
        const query = {...rest};

        query.business = isNum(business?.id) ? business.id : -1;

        return query;
      },
      filterFile(file) {
        return !/\.(csv)$/i.test(file.name);
      },
      onUploadFile(file) {
        const query = {
          csv: file
        };

        return this.$store.dispatch('billing/uploadCSV', query);
      },
      getCountries() {
        return this.$store.dispatch('config/getCountries');
      },
      getEuCountries() {
        return this.$store.dispatch('config/getEuCountries');
      },
    },
  };
</script>

<style lang="scss">
  @use '../../assets/styles/theme' as *;

  [data-view="billing"] {
    [data-component="file-upload"] {
      margin: 0 0 0 $spacing-xs;

      .v-btn {
        box-shadow: none;
      }
    }

    .v-datatable {
      min-width: $table-min-width;
    }
  }
</style>
